import React, {useEffect} from 'react';
import {useHistory} from 'react-router';
import {Button, Card, Container, Alert} from 'react-bootstrap';

import PageTitle from 'components/ui/PageTitle/PageTitle';
import Loader from 'components/ui/AmnisLoader/AmnisLoader';
import SubmitButton from 'components/ui/Button/SubmitButton';
import {useUpdateBusinessPartnerRes} from 'api/business-partner';
import {PATH} from 'constants/common';
import {useResolveInviteStore} from 'components/pages/ResolveInvitePage/resolveInviteStore';
import {useLanguage} from 'context/language-context';
import {useUpdateUser} from 'api/users';
import {useAuth} from 'context/auth-context';

import {useGetInviteData} from 'api/register';
import {BusinessPartnerInvite} from 'types/api/business-partner-invite';
import styles from './BusinessPartnerInvitePage.module.scss';

const BusinessPartnerInvitePage = () => {
    const history = useHistory();
    const hash = useResolveInviteStore(state => state.hash);
    const type = useResolveInviteStore(state => state.type);
    const reset = useResolveInviteStore(state => state.reset);
    const {
        data: inviteData,
        isLoading: isInviteDataLoading,
        isSuccess: isInviteDataSuccess,
        error: inviteDataError,
    } = useGetInviteData({hash, type});
    const {updateLanguage, availableLanguages} = useLanguage();
    const {user} = useAuth();
    const {mutate: updateUser} = useUpdateUser();
    const {mutate: updateBusinessPartner, isPending: isUpdateLoading} =
        useUpdateBusinessPartnerRes();

    const handleCancel = () => {
        reset();
        history.push(PATH.DASHBOARD);
    };

    const handleSubmit = () => {
        updateBusinessPartner(hash, {
            onSuccess: () => {
                reset();
                history.push(PATH.DASHBOARD);
            },
        });
    };

    useEffect(() => {
        if ((inviteDataError as any)?.['hydra:description'] === 'Not Found') {
            reset();
        }
    }, [inviteDataError, useResolveInviteStore, history]);

    useEffect(() => {
        if (isInviteDataSuccess) {
            if (!inviteData.active) {
                reset();
                history.push(PATH.DASHBOARD);
            }
        }
    }, [inviteData, isInviteDataSuccess, history]);

    useEffect(() => {
        if (
            inviteData &&
            inviteData.language &&
            isInviteDataSuccess &&
            !user?.language
        ) {
            const {language} = inviteData;
            const lang = availableLanguages.find(l => l.id === language)!;
            updateLanguage(lang.code);
            updateUser({'@id': user['@id'], language: lang.id});
        }
    }, [
        availableLanguages,
        inviteData,
        isInviteDataSuccess,
        updateLanguage,
        updateUser,
        user,
    ]);

    if (isInviteDataLoading) {
        return <Loader />;
    }

    return (
        <Container>
            <PageTitle>Invite</PageTitle>

            {inviteData &&
            (inviteData as BusinessPartnerInvite).businessPartner ? (
                <Card body>
                    <h2 className="table-title">
                        You are invited to&nbsp;
                        <var data-var="company-name">
                            {
                                (inviteData as BusinessPartnerInvite)
                                    .businessPartner.companyName
                            }
                        </var>
                    </h2>
                    <p>To confirm the invite please click the button</p>

                    <div className={styles.buttons}>
                        <Button
                            className="mr-2"
                            variant="outline-primary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                        <SubmitButton
                            onClick={handleSubmit}
                            isLoading={isUpdateLoading}
                        >
                            Confirm
                        </SubmitButton>
                    </div>
                </Card>
            ) : (
                <Alert variant="danger">
                    The invite link is not valid or it&apos;s used already
                </Alert>
            )}
        </Container>
    );
};

export default BusinessPartnerInvitePage;
